import React, { useMemo, useState } from "react";
import { Tooltip } from "@fluentui/react-components";
import { Info } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import DropdownFluent from "../controls/dropdown/DropdownFluent";
import { formatHours } from "./cardFormat";
import {
  BUDGET_STATUS_STYLE,
  PROJECT_TYPE_LABEL,
  ProjectType,
  formatMoney,
  formatPercent,
  formatSignedHours,
  formatSignedPercent,
  marginStatusStyle,
} from "./performanceFormat";
import styles from "./resourceInsights.module.scss";

const OVER_BUDGET = 1;
const LOSS = 1;
const BELOW_MINIMUM = 2;

/** The colour of a figure that is judged: healthy, to watch, or unhealthy. A figure that is only informative has none. */
const TONE_CLASS = { good: styles.metricGood, warning: styles.metricWarning, bad: styles.metricBad };

/** The tone of a GP status: a loss is unhealthy, a GP that is not above the minimum is to watch. */
function marginTone(marginStatus) {
  if (marginStatus === LOSS) return "bad";
  if (marginStatus === BELOW_MINIMUM) return "warning";
  return "good";
}

/** The tone of the forecast GP, which has no status of its own: judged against the minimum GP like the one at completion. */
function forecastGpTone(percent, minimumMarginPercent) {
  if (percent === null || percent === undefined) return undefined;
  if (percent < 0) return "bad";
  return percent <= minimumMarginPercent ? "warning" : "good";
}

/** One labelled figure of a project's budget, coloured when it says something about the health of the project. */
function Metric({ label, value, hint, tone }) {
  return (
    <Tooltip content={hint} relationship="description" positioning="above-start">
      <div className={`${styles.metric} ${tone ? TONE_CLASS[tone] : ""}`}>
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
    </Tooltip>
  );
}

function StatusChip({ look }) {
  return (
    <span className={styles.budgetChip} style={{ "--tone": look.color, "--tone-soft": look.soft }}>
      {look.label}
    </span>
  );
}

/** How much a project needs attention: a loss first, then a GP that is not above the minimum, then an overrun of hours. */
function severity(project) {
  if (project.margin?.status === LOSS) return 3;
  if (project.margin?.status === BELOW_MINIMUM) return 2;
  return project.status === OVER_BUDGET ? 1 : 0;
}

/** The problems of a project in words, to follow its name in the list. */
function flagsOf(project, minimumMarginPercent) {
  const flags = [];
  if (project.margin?.status === LOSS) flags.push("Loss");
  if (project.margin?.status === BELOW_MINIMUM) flags.push(marginStatusStyle(BELOW_MINIMUM, minimumMarginPercent).label);
  if (project.status === OVER_BUDGET) flags.push("Over budget");
  return flags;
}

/** The figures that say what a project's GP rests on, which depend on how the project is billed. */
function moneyMetrics(project, margin) {
  const tone = marginTone(margin.status);

  if (project.projectType === ProjectType.FIXED_PRICE) {
    return [
      { key: "bill", label: "Forecast bill", value: formatMoney(margin.forecastBill), hint: "What the project earns from this resource: the hours forecast at the bill rate. It does not grow with extra hours." },
      { key: "cost", label: "Cost at completion", value: formatMoney(margin.projectedCost), hint: "The actual cost so far plus the forecast cost of the months still to come.", tone },
    ];
  }

  return [
    {
      key: "billing",
      label: "Billing at completion",
      value: formatMoney(margin.projectedBilling),
      hint: "What is invoiced so far plus the forecast bill of the months still to come. The hours worked are billed as they are.",
      tone: margin.billingOverrunPercent > 0 ? "bad" : "good",
    },
    { key: "cost", label: "Cost at completion", value: formatMoney(margin.projectedCost), hint: "The actual cost so far plus the forecast cost of the months still to come.", tone },
  ];
}

/** The budget of the resource on one project: hours against the forecast and, with the cashflow in view, the GP. */
function ProjectBudget({ project, minimumMarginPercent, showHours }) {
  const budgetLook = BUDGET_STATUS_STYLE[project.status];
  const margin = project.margin;
  const marginLook = margin ? marginStatusStyle(margin.status, minimumMarginPercent) : null;
  const isFixedPrice = project.projectType === ProjectType.FIXED_PRICE;
  const hoursTone = project.status === OVER_BUDGET ? "bad" : "good";

  return (
    <>
      <div className={styles.budgetMeta}>
        <span className={styles.budgetType}>{PROJECT_TYPE_LABEL[project.projectType]}</span>
        <div className={styles.budgetChips}>
          <StatusChip look={budgetLook} />
          {marginLook && <StatusChip look={marginLook} />}
        </div>
      </div>

      <div className={styles.budgetMetrics}>
        {showHours && <Metric label="Actual hrs" value={formatHours(project.actualHours)} hint="Hours of validated timesheets, from the first month of the cashflow line to now." />}
        {showHours && (
          <Metric label="Forecast hrs at completion" value={formatHours(project.expectedHours)} hint="The actual hours so far plus the hours still forecast. The current month counts as the larger of the actual and the forecast." tone={hoursTone} />
        )}
        {showHours && <Metric label="Forecast hrs" value={formatHours(project.budgetHours)} hint="The hours forecast on the cashflow line over all its months. A month without a forecast takes the weekly share." />}
        <Metric
          label="Over forecast hrs %"
          value={`${showHours ? `${formatSignedHours(project.overrunHours)} · ` : ""}${formatSignedPercent(project.overrunPercent)}`}
          hint="Forecast hours at completion against forecast hours. Any excess puts the budget over."
          tone={hoursTone}
        />

        {margin && margin.status !== 0 && (
          <>
            <Metric
              label="Forecast GP %"
              value={formatPercent(margin.plannedMarginPercent)}
              hint="The GP the forecast promises: forecast bill minus forecast cost, over the forecast bill."
              tone={forecastGpTone(margin.plannedMarginPercent, minimumMarginPercent)}
            />
            <Metric
              label="GP % at completion"
              value={formatPercent(margin.projectedMarginPercent)}
              hint={isFixedPrice ? "Forecast bill minus the cost at completion, over the forecast bill." : "Billing at completion minus the cost at completion, over the billing. On time and materials it follows the rates, so extra hours do not erode it."}
              tone={marginTone(margin.status)}
            />
            {moneyMetrics(project, margin).map((metric) => (
              <Metric key={metric.key} label={metric.label} value={metric.value} hint={metric.hint} tone={metric.tone} />
            ))}
            {isFixedPrice && showHours && margin.headroomHours !== null && margin.headroomHours !== undefined && (
              <Metric
                label="Hours to break-even"
                value={formatSignedHours(margin.headroomHours)}
                hint={`The hours the resource can still work before their cost reaches the forecast bill (${formatHours(margin.breakEvenHours)} in all). Negative once they are expected to cost more than they earn.`}
                tone={margin.headroomHours < 0 ? "bad" : "good"}
              />
            )}
            {!isFixedPrice && margin.billingOverrunPercent !== null && margin.billingOverrunPercent !== undefined && (
              <Metric label="Billing over forecast %" value={formatSignedPercent(margin.billingOverrunPercent)} hint="How far the billing at completion is above the forecast bill." tone={margin.billingOverrunPercent > 0 ? "bad" : "good"} />
            )}
          </>
        )}
      </div>

      {margin && margin.status === 0 && <p className={styles.note}>No rates on the cashflow line, so no GP can be worked out.</p>}
    </>
  );
}

/** The rules the statuses follow, as a tooltip, with the thresholds the server applies. */
function rulesText(budget) {
  const tolerance = budget.budgetOverrunTolerancePercent;
  const minimum = budget.minimumMarginPercent;

  return (
    `Cumulative: from the first month of each cashflow line to its last. Months gone by are the actual, the current month is the larger of the actual and the forecast, months to come are the forecast. ` +
    `Over budget: the forecast hours at completion exceed the forecast hours${tolerance > 0 ? ` by more than ${tolerance} %` : " by any amount"}. ` +
    `GP: fixed price projects keep the forecast bill whatever the hours, so extra hours eat into the GP; time and materials projects bill the hours worked, so the GP follows the rates. ` +
    `A loss is a GP below zero${minimum > 0 ? `, and a GP of ${minimum} % or less is flagged too` : ", and a GP of exactly zero is flagged as no GP"}. ` +
    `Green figures are healthy, amber ones need watching, red ones are unhealthy.`
  );
}

/** What is wrong across all the projects, in one line, so a project that is not the one chosen is never out of sight. */
function Summary({ projects }) {
  const losses = projects.filter((project) => project.margin?.status === LOSS).length;
  const noGp = projects.filter((project) => project.margin?.status === BELOW_MINIMUM).length;
  const over = projects.filter((project) => project.status === OVER_BUDGET).length;

  const parts = [
    over > 0 && { key: "over", text: `${over} over budget`, tone: styles.summaryBad },
    losses > 0 && { key: "loss", text: `${losses} ${losses === 1 ? "loss" : "losses"}`, tone: styles.summaryBad },
    noGp > 0 && { key: "noGp", text: `${noGp} with no GP`, tone: styles.summaryWarning },
  ].filter(Boolean);

  return (
    <p className={styles.budgetSummary}>
      <span>{projects.length} projects</span>
      {parts.length === 0 ? <span className={styles.summaryGood}>all within budget</span> : parts.map((part) => <span key={part.key} className={part.tone}>{part.text}</span>)}
    </p>
  );
}

/**
 * How the resource stands against the hours forecast for them, accumulated over the whole cashflow line, and, for
 * those who may see the cashflow, whether the forecast keeps them profitable. One project is shown
 * at a time, chosen from a list that puts the ones needing attention first, and the figures are coloured by whether
 * they are healthy. It does not follow the period of Resource Insights.
 *
 * @param state  `{ status: "loading" | "ready" | "error", budget, reload }`, as `useResourceBudget` returns it
 * @param showHours  whether the figures made of hours are shown
 */
export default function BudgetSection({ state, showHours }) {
  const { status, budget, reload } = state;
  const [chosenId, setChosenId] = useState(null);

  // The projects needing attention come first, the others by name.
  const projects = useMemo(() => [...(budget?.projects ?? [])].sort((a, b) => severity(b) - severity(a) || a.projectName.localeCompare(b.projectName)), [budget]);
  const selected = projects.find((project) => project.projectId === chosenId) ?? projects[0];
  const options = useMemo(
    () =>
      projects.map((project) => {
        const flags = flagsOf(project, budget.minimumMarginPercent);
        return { name: flags.length > 0 ? `${project.projectName} · ${flags.join(", ")}` : project.projectName, value: project.projectId };
      }),
    [projects, budget]
  );

  const title = (
    <h4>
      Budget
      {budget && (
        <Tooltip content={rulesText(budget)} relationship="description" positioning="above">
          <span className={styles.infoIcon}>
            <Info size={13} />
          </span>
        </Tooltip>
      )}
    </h4>
  );

  if (status === "error") {
    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          {title}
          <span className={styles.sectionHint}>Cumulative · actual to date + forecast</span>
        </div>
        <div className={styles.errorBox}>
          <span>The budget could not be loaded.</span>
          <button type="button" className={styles.linkButton} onClick={reload}>
            Try again
          </button>
        </div>
      </section>
    );
  }

  if (!budget) {
    return (
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          {title}
          <span className={styles.sectionHint}>Cumulative · actual to date + forecast</span>
        </div>
        <Skeleton height={96} borderRadius={12} />
      </section>
    );
  }

  return (
    <section className={`${styles.section} ${status === "loading" ? styles.refreshing : ""}`}>
      <div className={styles.sectionHeader}>
        {title}
        <span className={styles.sectionHint}>Cumulative · actual to date + forecast</span>
      </div>

      {projects.length === 0 ? (
        <p className={styles.note}>No forecast or hours worked yet on the cashflow lines of the projects you can see.</p>
      ) : (
        <div className={styles.budgetCard}>
          {projects.length > 1 ? (
            <DropdownFluent
              key={selected.projectId}
              options={options}
              defaultVal={selected.projectName}
              selectedOptions={[selected.projectId]}
              showLabel={false}
              placeholder="Choose a project"
              StyleClass={styles.budgetSelect}
              onChange={setChosenId}
            />
          ) : (
            <strong className={styles.budgetName}>{selected.projectName}</strong>
          )}

          {projects.length > 1 && <Summary projects={projects} />}

          <ProjectBudget project={selected} minimumMarginPercent={budget.minimumMarginPercent} showHours={showHours} />
        </div>
      )}

      {!budget.showsMoney && projects.length > 0 && <p className={styles.note}>GP figures are shown to people who can see the cashflow.</p>}
    </section>
  );
}
